

import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { PlusCircle, Filter  } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Button } from '../components/ui/button';


export default function InstitutionDashboard() {
  const { axiosInstance, user } = useAuth();
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [newPrestation, setNewPrestation] = useState({
    name: '',
    description: '',
    dateStart: '',
    dateEnd: '',
    type_soignant: '',
    pricing_type: 'normal',
  });
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [selectedSoignant, setSelectedSoignant] = useState('');
  const [selectedFile, setSelectedFile] = useState({});
  const [dashboard, setDashboard] = useState(null);


  useEffect(() => {
    fetchPrestations();
    fetchDashboard();
  }, []);

  
  const fetchPrestations = async () => {
    try {
      const params = new URLSearchParams();
      if (dateStart) params.append('start_date', dateStart);
      if (dateEnd) params.append('end_date', dateEnd);
      if (selectedSoignant) params.append('soignant_id', selectedSoignant);
      
      const response = await axiosInstance.get(`/prestations/institution?${params}`);
      setPrestations(response.data || []);
    } catch (error) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axiosInstance.get('/prestations/institution/dashboard');
      setDashboard(res.data);
    } catch (err) {
      toast.error("Erreur dashboard");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR');
  };

  const countByStatus = (statuses) =>
    prestations.filter((p) => statuses.includes(p.status)).length;

  const handleCreatePrestation = async (e) => {
    e.preventDefault();
    if (!newPrestation.name || !newPrestation.dateStart || !newPrestation.dateEnd || !newPrestation.type_soignant) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    try {
      const payload = {
        name: newPrestation.name,
        description: newPrestation.description,
        dateStart: new Date(newPrestation.dateStart).toISOString(),
        dateEnd: new Date(newPrestation.dateEnd).toISOString(),
        type_soignant: newPrestation.type_soignant,
        pricing_type: newPrestation.pricing_type,
        etablissement_id: user.id,
      };
      const res = await axiosInstance.post('/prestations', payload);
      toast.success(res.data.message || 'Prestation soumise à l’administrateur.');
      setOpenModal(false);
      setNewPrestation({ name: '', description: '', dateStart: '', dateEnd: '', type_soignant: '', pricing_type: 'normal' });
      fetchPrestations();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la création de la prestation.');
    }
  };

  if (!user) return null;



  const handleDocDownload = async (userId, docName) => {
    try {
      const res = await axiosInstance.get(
        `/users/${userId}/documents/${docName}`
      );

      window.open(res.data.url, "_blank");
    } catch (err) {
      toast.error("Document indisponible");
    }
  };


  const handleUserDocUpload = async (userId, docName, file) => {
    if (!file) return toast.error("Fichier manquant");

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axiosInstance.post(
        `/users/${userId}/upload/${docName}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Document téléversé");
    } catch (err) {
      console.error(err);
      toast.error("Erreur upload document");
    }
  };



  return (
    <Layout title={`Bienvenue, ${user.first_name || user.name || 'Établissement'} !`}>
      <div className="space-y-8">
        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardHeader><CardTitle>Total</CardTitle></CardHeader>
            <CardContent>{dashboard?.stats?.total || 0}</CardContent>
          </Card>

          {/* <Card>
            <CardHeader><CardTitle>Assignées</CardTitle></CardHeader>
            <CardContent>{dashboard?.stats?.assigned || 0}</CardContent>
          </Card> */}

          {/* <Card>
            <CardHeader><CardTitle>En cours</CardTitle></CardHeader>
            <CardContent>{dashboard?.stats?.in_progress || 0}</CardContent>
          </Card> */}

          <Card>
            <CardHeader><CardTitle>Terminées</CardTitle></CardHeader>
            <CardContent>{dashboard?.stats?.completed || 0}</CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Prestations du jour</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.today_prestations?.length === 0 ? (
              <p>Aucune prestation aujourd’hui</p>
            ) : (
              <div className="space-y-3">
                {dashboard?.today_prestations?.map((p, i) => (
                  <div key={i} className="p-3 border rounded-lg bg-gray-50">
                    <div className="font-semibold">{p.name}</div>

                    <div className="text-sm text-gray-600">
                      {formatDate(p.dateStart)} → {formatDate(p.dateEnd)}
                    </div>

                    <div className="text-sm mt-1">
                      👨‍⚕️ {p.assigned_name || "Non assigné"} ({p.role})
                    </div>

                    <div className="text-xs text-gray-500">
                      Statut : {p.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bouton création prestation */}
        <div className="flex justify-end">
          <button
            onClick={() => setOpenModal(true)}
            className="flex items-center gap-2 bg-[#6FAFAF] hover:bg-[#4F8585] text-white px-4 py-2 rounded-lg transition"
          >
            <PlusCircle size={20} />
            Créer une prestation
          </button>
        </div>

        {/* Liste des prestations */}
        <Card className="glass-card border-[#C1DDDD]">
          <CardHeader>
            <CardTitle>Liste des prestations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Chargement...</p>
            ) : prestations.length === 0 ? (
              <p>Aucune prestation pour le moment.</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <input 
                  type="date" 
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className="border p-2 rounded"
                  max={dateEnd || new Date().toISOString().split('T')[0]}
                />
                <input 
                  type="date" 
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="border p-2 rounded"
                  min={dateStart}
                />
                <button 
                  onClick={fetchPrestations}
                  className="bg-[#6FAFAF] hover:bg-[#4F8585] text-white px-4 py-2 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <Filter size={18} />  {/* Import lucide-react Filter */}
                  Filtrer
                </button>
                              </div>
                <table className="min-w-full border border-gray-200">
                  <thead className="bg-[#D4EBEB]">
                    <tr>
                      <th className="px-4 py-2 text-left">Nom</th>
                      <th className="px-4 py-2 text-left">Statut</th>
                      <th className="px-4 py-2 text-left">Date de début</th>
                      <th className="px-4 py-2 text-left">Date de fin</th>
                      <th className="px-4 py-2 text-left">Assigné à</th>
                      <th className="px-4 py-2 text-left">Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prestations.map((p) => (
                      <tr key={p._id} className="border-t hover:bg-[#F4FAFA] transition">
                        <td className="px-4 py-2 font-medium">{p.name || '—'}</td>
                        <td className="px-4 py-2 capitalize">{p.status || '—'}</td>
                        <td className="px-4 py-2">{formatDate(p.dateStart)}</td>
                        <td className="px-4 py-2">{formatDate(p.dateEnd)}</td>
                        <td className="px-4 py-2">{p.assigned_name || '—'}</td>
                        <td className="px-4 py-2">
  {p.assigned_name && p.assigned_to ? (
    <div className="space-y-1 max-w-xs">
      <span className="text-sm font-medium block mb-1 truncate">
        {p.assigned_name}
      </span>

      <div className="flex flex-wrap gap-1">
  {[
    { key: 'cv', label: 'CV' },
    { key: 'identity_card', label: 'ID' },
    { key: 'visa_inami', label: 'INAMI' },
    { key: 'diplomas', label: 'Dipl.' },
    { key: 'certificat_bonne_moeurs', label: 'CBM' }
  ].map(({ key, label }) => {
    const doc = p.caregiver_documents?.[key];

    if (!doc?.url) return null;

    return (
      <button
        key={key}
        onClick={() => window.open(doc.url, "_blank")}
        className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded-full"
      >
        {label}
      </button>
    );
  })}
</div>
    </div>
  ) : (
    <span className="text-gray-400 text-sm">—</span>
  )}
</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal création */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Card className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-[#3F7373]">
                  Nouvelle prestation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreatePrestation} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nom *</label>
                    <input
                      type="text"
                      value={newPrestation.name}
                      onChange={(e) => setNewPrestation({ ...newPrestation, name: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={newPrestation.description}
                      onChange={(e) => setNewPrestation({ ...newPrestation, description: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                      rows="3"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date de début *</label>
                      <input
                        type="datetime-local"
                        value={newPrestation.dateStart}
                        onChange={(e) => setNewPrestation({ ...newPrestation, dateStart: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date de fin *</label>
                      <input
                        type="datetime-local"
                        value={newPrestation.dateEnd}
                        onChange={(e) => setNewPrestation({ ...newPrestation, dateEnd: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type de soignant *</label>
                    <select
                      value={newPrestation.type_soignant}
                      onChange={(e) => setNewPrestation({ ...newPrestation, type_soignant: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                      required
                    >
                      <option value="" disabled>— Sélectionnez un type —</option>
                      <option value="nurse">Infirmier</option>
                      <option value="caregiver">Aide-soignant</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type de tarification</label>
                    <select
                      value={newPrestation.pricing_type}
                      onChange={(e) => setNewPrestation({ ...newPrestation, pricing_type: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-[#6FAFAF] focus:border-[#6FAFAF]"
                    >
                      <option value="normal">Tarification normale (tarif fixe base BD)</option>
                      <option value="pourcentage">Tarification en pourcentage (majoration horaire)</option>
                    </select>
                  </div>

                  <div className="flex justify-end mt-6 space-x-3">
                    <button
                      type="button"
                      onClick={() => setOpenModal(false)}
                      className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#6FAFAF] text-white rounded-lg hover:bg-[#4F8585]"
                    >
                      Soumettre
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </Dialog>
      </div>
    </Layout>
  );
}